import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * EntityDetailLayout — the two-column scaffold of a detail page: a fixed-width
 * `sidebar` (a {@link DetailPanel}) beside the main content column (a
 * {@link DetailHeader} + the record's activity / body). It's just the frame; the
 * two pieces are composed by the caller.
 *
 *   <EntityDetailLayout sidebar={<DetailPanel>…</DetailPanel>}>
 *     <DetailHeader identifier="10482" title="Q3 Reorder" … />
 *     <main>…activity…</main>
 *   </EntityDetailLayout>
 *
 * Columns align to the top by default (`items-start`) so the sidebar keeps its
 * natural height while the main column drives page length. In the app, wrap it
 * in a height-bounded parent (`h-full`) and add `scroll` for the split-scroll
 * behaviour — each column then scrolls independently (the sidebar via its own
 * `DetailPanel` overflow, the main column here).
 */

export interface EntityDetailLayoutProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Left column — typically a `DetailPanel`. */
  sidebar: React.ReactNode
  /** Main column — a `DetailHeader` and the record's body. */
  children: React.ReactNode
  /**
   * Fill the parent's height and let each column scroll independently. Requires
   * a height-bounded parent. Default `false` (columns grow; the page scrolls).
   */
  scroll?: boolean
}

export const EntityDetailLayout = React.forwardRef<
  HTMLDivElement,
  EntityDetailLayoutProps
>(({ sidebar, children, scroll, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex gap-6',
      scroll ? 'h-full min-h-0 items-stretch' : 'items-start',
      className
    )}
    {...props}
  >
    {sidebar}
    <div
      className={cn(
        'min-w-0 flex-1',
        scroll && 'fbui-scroll overflow-y-auto'
      )}
    >
      {children}
    </div>
  </div>
))
EntityDetailLayout.displayName = 'EntityDetailLayout'
